import { supabase } from './supabase';
import { Profile, Note, Filters, Dispo } from '../types'; // eslint-disable-line @typescript-eslint/no-unused-vars

// Service API pour les opérations CRUD

// =============================================================================
// Fonctions de mapping pour résoudre les incompatibilités frontend/backend
// =============================================================================

// Mapping des valeurs du frontend vers la base de données
function mapProfileDataForDB(profileData: any) {
  if (!profileData) return profileData;

  return {
    ...profileData,
    // Mapping pour le champ sexe : frontend 'homme'/'femme' -> DB 'garcon'/'fille'
    sexe: profileData.sexe === 'homme' ? 'garcon' :
          profileData.sexe === 'femme' ? 'fille' :
          profileData.sexe // garde 'non_specifie' tel quel
  };
}

// Mapping des valeurs de la base de données vers le frontend
function mapProfileDataFromDB(profileData: any) {
  if (!profileData) return profileData;

  return {
    ...profileData,
    // Mapping inverse pour le champ sexe : DB 'garcon'/'fille' -> frontend 'homme'/'femme'
    sexe: profileData.sexe === 'garcon' ? 'homme' :
          profileData.sexe === 'fille' ? 'femme' :
          profileData.sexe // garde 'non_specifie' tel quel
  };
}

// CACHE SUPPRIMÉ - Cause des boucles infinies
// Plus de cache pour éviter tous les problèmes

export const api = {
  // Helper pour récupérer l'ID du profil de l'utilisateur connecté
  async getCurrentUserProfileId(): Promise<{ data: string | null; error: any }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        return { data: null, error: 'Utilisateur non connecté' };
      }

      const { data: profile, error } = await supabase
        .from('profils')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        return { data: null, error };
      }

      if (!profile) {
        return { data: null, error: null }; // Aucun profil trouvé
      }

      return { data: profile.id, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },

  // Test de connexion
  async testConnection() {
    try {
      const { data, error } = await supabase
        .from('tuteurs_public')
        .select('id')
        .limit(1);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  // Authentification
  auth: {
    async signInWithOtp(email: string, captchaToken?: string) {

      // ⚠️ IMPORTANT: Utiliser www. dans l'URL pour correspondre à la config Supabase
      const redirectUrl = 'https://www.tutormatch-heg-ge.ch/login';

      const result = await supabase.auth.signInWithOtp({
        email,
        options: {
          // Rediriger vers la page login avec code PKCE
          emailRedirectTo: redirectUrl,
          captchaToken // Token hCaptcha pour la vérification serveur
        }
      });

      if (result.error) {
      } else {
      }

      return result;
    },

    async signOut() {
      const result = await supabase.auth.signOut();

      if (result.error) {
      } else {
      }

      return result;
    },

    async getUser() {
      try {
        const result = await supabase.auth.getUser();

        if (result.error) {
          // Ne pas logger les erreurs de session manquante comme des erreurs critiques
          if (result.error.message.includes('session missing')) {
            // Session manquante - pas d'action nécessaire
          } else {
          }
        } else if (result.data.user) {
        }

        return result;
      } catch (err: any) {
        return { data: { user: null }, error: null };
      }
    }
  },

  // Profils
  profiles: {
    async getByUserId(userId: string) {

      try {
        const result = await supabase
          .from('profils')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (result.error) {
          return { data: null, error: result.error };
        }


        // MAPPING : Convertir les valeurs backend vers frontend
        let finalData = null;
        if (result.data) {
          finalData = mapProfileDataFromDB(result.data);
        }

        return { data: finalData, error: result.error };
      } catch (err) {
        return { data: null, error: err };
      }
    },

    async create(profile: Partial<Profile>, userOverride?: any) {
      try {
        let user = userOverride;

        // Si pas d'utilisateur fourni, vérifier la session courante
        if (!user) {
          const { data: { user: sessionUser }, error: sessionError } = await supabase.auth.getUser();
          if (sessionError) {
            throw new Error('Erreur de session: ' + sessionError.message);
          }
          if (!sessionUser) {
            throw new Error('Utilisateur non connecté - aucune session active');
          }
          user = sessionUser;
        }

        // S'assurer que user_id correspond à l'utilisateur
        const profileData = {
          ...profile,
          user_id: user.id
        };

        // MAPPING : Convertir les valeurs frontend vers backend
        const mappedData = mapProfileDataForDB(profileData);

        const result = await supabase
          .from('profils')
          .insert(mappedData)
          .select();

        if (result.error) {
        } else {
          // Cache supprimé - plus de gestion de cache
        }

        return result;
      } catch (err) {
        return { data: null, error: err };
      }
    },

    async update(profileId: string, updates: Partial<Profile>) {

      // MAPPING : Convertir les valeurs frontend vers backend
      const mappedUpdates = mapProfileDataForDB(updates);

      const result = await supabase
        .from('profils')
        .update(mappedUpdates)
        .eq('id', profileId);

      if (result.error) {
      } else {
        // Cache supprimé - plus de gestion de cache
      }

      return result;
    },

    async updateRole(profileId: string, newRole: 'etudiant' | 'repetiteur' | 'les_deux') {
      try {
        const updates: any = { role: newRole };

        // Si on passe d'étudiant à tuteur, on doit ajouter un tarif par défaut
        if (newRole === 'repetiteur' || newRole === 'les_deux') {
          // Récupérer le profil actuel pour vérifier s'il a déjà un tarif
          const { data: currentProfile } = await supabase
            .from('profils')
            .select('tarif')
            .eq('id', profileId)
            .single();

          if (currentProfile && currentProfile.tarif === 0) {
            updates.tarif = 30; // Tarif par défaut
          }
        }

        // Si on passe à étudiant uniquement, on peut mettre le tarif à 0
        if (newRole === 'etudiant') {
          updates.tarif = 0;
        }

        const result = await supabase
          .from('profils')
          .update(updates)
          .eq('id', profileId)
          .select();

        if (result.error) {
        } else {
          // Cache supprimé - plus de gestion de cache
        }

        return result;
      } catch (err) {
        return { data: null, error: err };
      }
    },

    async delete(profileId: string) {

      try {
        // Supprimer le profil (les contraintes CASCADE supprimeront automatiquement dispos, notes, matchs)
        const result = await supabase
          .from('profils')
          .delete()
          .eq('id', profileId);

        if (result.error) {
        } else {
          // Cache supprimé - plus de gestion de cache
        }

        return result;
      } catch (err) {
        return { data: null, error: err };
      }
    },

    async getTutors(filters?: Filters): Promise<{ data: Profile[] | null; error: any }> {

      // Utiliser la vue sécurisée tuteurs_public (sans nom, email, téléphone)
      let query = supabase
        .from('tuteurs_public')
        .select('*');

      // Application des filtres
      if (filters) {

        if (filters.filiere) {
          query = query.eq('filiere', filters.filiere);
        }

        if (filters.matiere) {
          query = query.ilike('matieres', `%${filters.matiere}%`);
        }

        if (filters.langue) {
          query = query.eq('langue', filters.langue);
        }

        if (filters.sexe) {
          query = query.eq('sexe', filters.sexe);
        }

        if (filters.tarif_max) {
          query = query.lte('tarif', filters.tarif_max);
        }

        // Note: Les filtres jour/créneau seront appliqués après récupération
        // car nous devons d'abord récupérer les disponibilités depuis la table pivot
      }

      query = query.order('created_at', { ascending: false });

      const result = await query;

      if (result.error) {
        return result;
      }

      // Récupérer les disponibilités et les notes pour chaque tuteur
      if (result.data && result.data.length > 0) {
        const tutorIds = result.data.map((t: any) => t.id);

        // Récupérer toutes les notes en une seule requête (accessible publiquement)
        const { data: allNotes, error: notesError } = await supabase
          .from('notes')
          .select('*')
          .in('target_id', tutorIds);

        // Si erreur d'accès, essayer sans filtre (pour déboguer)
        if (notesError && notesError.code === '42501') {
          const { data: allNotesDebug, error: notesErrorDebug } = await supabase
            .from('notes')
            .select('*')
            .limit(5);
          void allNotesDebug;
          void notesErrorDebug;
        }

        if (notesError) {
          // Procéder sans les notes si erreur
        }

        // Grouper les notes par tuteur
        const notesMap = new Map<string, any[]>();
        if (allNotes) {
          allNotes.forEach(note => {
            if (!notesMap.has(note.target_id)) {
              notesMap.set(note.target_id, []);
            }
            notesMap.get(note.target_id)?.push(note);
          });
        }

        const tutorsWithData: Profile[] = await Promise.all(
          result.data.map(async (tutor: Profile) => {
            try {
              // Utiliser directement supabase au lieu de api.dispos pour éviter la récursion
              const disposResult = await supabase
                .from('dispos')
                .select('*')
                .eq('tuteur_id', tutor.id)
                .order('jour', { ascending: true });

              if (disposResult.error) {
                return { ...tutor, dispos: [], notes: notesMap.get(tutor.id) || [] };
              }

              // Récupérer les notes pour ce tuteur
              const tutorNotes = notesMap.get(tutor.id) || [];

              return {
                ...tutor,
                dispos: (disposResult.data || []),
                notes: tutorNotes
              } as Profile;
            } catch (error) {
              return {
                ...tutor,
                dispos: [],
                notes: notesMap.get(tutor.id) || []
              } as Profile;
            }
          })
        );


        // Filtrer par disponibilités si nécessaire
        let filteredTutors = tutorsWithData;

        // Nouveau système de filtrage par disponibilités multiples
        if (filters && filters.disponibilites) {

          filteredTutors = filteredTutors.filter(tutor => {
            if (!tutor.dispos || tutor.dispos.length === 0) {
              return false; // Pas de disponibilités = pas dans les résultats
            }

            // Vérifier si le tuteur correspond à AU MOINS UNE des disponibilités demandées
            return Object.entries(filters.disponibilites!).some(([jour, creneaux]) => {
              return creneaux.some(creneau => {
                return tutor.dispos!.some(dispo =>
                  dispo.jour === jour && dispo.creneau === creneau
                );
              });
            });
          });

        }
        // Ancien système de filtrage (pour compatibilité)
        else if (filters && (filters.jour || filters.creneau)) {

          filteredTutors = tutorsWithData.filter(tutor => {
            if (!tutor.dispos || tutor.dispos.length === 0) {
              return false; // Pas de disponibilités = pas dans les résultats
            }

            return tutor.dispos.some(dispo => {
              const jourMatch = !filters.jour || dispo.jour === filters.jour;
              const creneauMatch = !filters.creneau || dispo.creneau === filters.creneau;
              return jourMatch && creneauMatch;
            });
          });

        }

        return { ...result, data: filteredTutors };
      }

      return result;
    }
  },

  // Notes
  notes: {
    async create(note: Partial<Note>) {
      const result = await supabase
        .from('notes')
        .upsert(note, { onConflict: 'auteur_id,target_id' })
        .select()
        .single();

      if (result.error) {
      } else {
      }

      return result;
    },

    async getByTutorId(tutorId: string) {
      const result = await supabase
        .from('notes')
        .select('*')
        .eq('target_id', tutorId)
        .order('created_at', { ascending: false });

      if (result.error) {
      }

      return result;
    }
  },

  // Gestion des disponibilités (table dispos séparée)
  dispos: {
    // Cache pour les valeurs enum détectées
    _enumCache: null as any,

    async detectEnumValues(): Promise<{ jours: string[], creneaux: string[] }> {
      if (this._enumCache) {
        return this._enumCache;
      }

      try {

        // Essayer de récupérer les valeurs enum via une requête RPC si disponible
        const { data: creneauxData, error: creneauxError } = await supabase
          .rpc('get_creneaux_enum')
          .limit(1);

        const { data: joursData, error: joursError } = await supabase
          .rpc('get_jours_enum')
          .limit(1);

        if (!creneauxError && !joursError && creneauxData && joursData) {
          this._enumCache = {
            jours: joursData,
            creneaux: creneauxData
          };
          return this._enumCache;
        }
      } catch (error) {
      }

      // Valeurs par défaut si la détection échoue
      this._enumCache = {
        jours: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'],
        creneaux: ['matin', 'apm', 'soir']
      };

      return this._enumCache;
    },
    async getByTuteurId(tuteurId: string): Promise<{ data: Dispo[] | null; error: any }> {

      if (!tuteurId || typeof tuteurId !== 'string') {
        return { data: null, error: { message: 'ID tuteur invalide' } };
      }

      try {
        const result = await supabase
          .from('dispos')
          .select('*')
          .eq('tuteur_id', tuteurId)
          .order('jour', { ascending: true });

        if (result.error) {
          return { data: null, error: result.error };
        }

        return { data: result.data || [], error: null };
      } catch (error) {
        return { data: null, error };
      }
    },

    async create(tuteurId: string, jour: string, creneau: string): Promise<{ data: Dispo | null; error: any }> {

      if (!tuteurId || !jour || !creneau) {
        return { data: null, error: { message: 'Paramètres manquants' } };
      }

      try {
        const result = await supabase
          .from('dispos')
          .insert({
            tuteur_id: tuteurId,
            jour: jour,
            creneau: creneau
          })
          .select()
          .single();

        if (result.error) {
          return { data: null, error: result.error };
        }

        return { data: result.data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },

    async delete(id: string): Promise<{ error: any }> {

      if (!id) {
        return { error: { message: 'ID manquant' } };
      }

      try {
        const result = await supabase
          .from('dispos')
          .delete()
          .eq('id', id);

        if (result.error) {
          return { error: result.error };
        }

        return { error: null };
      } catch (error) {
        return { error };
      }
    },

    async deleteByTuteurAndSlot(tuteurId: string, jour: string, creneau: string): Promise<{ error: any }> {

      if (!tuteurId || !jour || !creneau) {
        return { error: { message: 'Paramètres manquants' } };
      }

      try {
        const result = await supabase
          .from('dispos')
          .delete()
          .eq('tuteur_id', tuteurId)
          .eq('jour', jour)
          .eq('creneau', creneau);

        if (result.error) {
          return { error: result.error };
        }

        return { error: null };
      } catch (error) {
        return { error };
      }
    },

    async updateForTuteur(tuteurId: string, disponibilites: Array<{jour: string, creneau: string}>): Promise<{ error: any }> {

      if (!tuteurId || typeof tuteurId !== 'string') {
        return { error: { message: 'ID tuteur invalide' } };
      }

      // Fonction de normalisation des créneaux
      const normalizeCreneaux = (creneau: string): string => {
        const creneauMap: Record<string, string> = {
          'matin': 'matin',
          'apres_midi': 'apm',
          'apres-midi': 'apm',
          'après-midi': 'apm',
          'après_midi': 'apm',
          'apm': 'apm',
          'soir': 'soir'
        };
        return creneauMap[creneau] || creneau;
      };

      // Validation des disponibilités
      const validJours = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
      const validCreneaux = ['matin', 'apm', 'soir'];

      // Normaliser et valider les disponibilités
      const normalizedDisponibilites = [];
      for (const dispo of disponibilites) {
        if (!dispo || typeof dispo !== 'object') {
          return { error: { message: 'Format de disponibilité invalide' } };
        }

        if (!validJours.includes(dispo.jour)) {
          return { error: { message: `Jour invalide: ${dispo.jour}` } };
        }

        const normalizedCreneau = normalizeCreneaux(dispo.creneau);
        if (!validCreneaux.includes(normalizedCreneau)) {
          return { error: { message: `Créneau invalide: ${dispo.creneau}` } };
        }

        normalizedDisponibilites.push({
          jour: dispo.jour,
          creneau: normalizedCreneau
        });
      }

      // Fonction de retry pour les opérations critiques
      const retryOperation = async <T>(operation: () => Promise<T>, maxRetries = 3): Promise<T> => {
        let lastError: any;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            return await operation();
          } catch (error) {
            lastError = error;

            if (attempt < maxRetries) {
              // Attendre avant de réessayer (backoff exponentiel)
              await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
            }
          }
        }

        throw lastError;
      };

      try {
        // 1. Supprimer les anciennes disponibilités avec retry
        await retryOperation(async () => {
          const { error: deleteError } = await supabase
            .from('dispos')
            .delete()
            .eq('tuteur_id', tuteurId);

          if (deleteError) {
            throw deleteError;
          }
        });

        // 2. Insérer les nouvelles disponibilités (si il y en a)
        if (normalizedDisponibilites.length > 0) {
          // Éliminer les doublons
          const uniqueDispos = normalizedDisponibilites.filter((dispo, index, self) =>
            index === self.findIndex(d => d.jour === dispo.jour && d.creneau === dispo.creneau)
          );

          const disposToInsert = uniqueDispos.map(dispo => ({
            tuteur_id: tuteurId,
            jour: dispo.jour,
            creneau: dispo.creneau
          }));

          // Insérer avec retry
          await retryOperation(async () => {
            const { error: insertError } = await supabase
              .from('dispos')
              .insert(disposToInsert);

            if (insertError) {
              throw insertError;
            }
          });

        } else {
        }

        return { error: null };
      } catch (error: any) {

        // Retourner un message d'erreur plus explicite
        const errorMessage = error?.message || 'Erreur inconnue lors de la mise à jour';
        return {
          error: {
            message: errorMessage,
            details: error,
            code: error?.code || 'UNKNOWN_ERROR'
          }
        };
      }
    },

    async getAllAvailable(): Promise<{ data: Dispo[] | null; error: any }> {

      try {
        const result = await supabase
          .from('dispos')
          .select('*, profils!inner(prenom, nom, filiere)')
          .order('jour', { ascending: true });

        if (result.error) {
          return { data: null, error: result.error };
        }

        return { data: result.data || [], error: null };
      } catch (error) {
        return { data: null, error };
      }
    }
  },

  // Gestion des matchs
  matchs: {
    async create(etudiantId: string, repetiteurId: string, liked: boolean) {
      const result = await supabase
        .from('matchs')
        .insert({
          etudiant_id: etudiantId,
          repetiteur_id: repetiteurId,
          liked
        });

      if (result.error) {
      } else {
      }

      return result;
    },

    async getByEtudiantId(etudiantId: string) {
      const result = await supabase
        .from('matchs')
        .select('*')
        .eq('etudiant_id', etudiantId);

      if (result.error) {
      }

      return result;
    }
  },

  // API pour les demandes de contact
  contactRequests: {
    async create(studentId: string, tutorId: string, message?: string) {
      try {
        const result = await supabase
          .from('contact_requests')
          .insert({
            student_id: studentId,
            tutor_id: tutorId,
            message: message || '',
            status: 'pending'
          })
          .select();

        if (result.error) {
        } else {
        }

        return result;
      } catch (err) {
        return { data: null, error: err };
      }
    },

    async getByTutorId(tutorId: string) {
      try {
        const result = await supabase
          .from('contact_requests')
          .select(`
            *,
            student_profile:student_id (
              id, prenom, nom, filiere, annee, user_id,
              user:user_id (email)
            )
          `)
          .eq('tutor_id', tutorId)
          .order('created_at', { ascending: false });

        if (result.error) {
        }

        return result;
      } catch (err) {
        return { data: null, error: err };
      }
    },

    async getByStudentId(studentId: string) {
      try {
        const result = await supabase
          .from('contact_requests')
          .select(`
            *,
            tutor_profile:tutor_id (
              id, prenom, nom, filiere, tarif, user_id,
              user:user_id (email)
            )
          `)
          .eq('student_id', studentId)
          .order('created_at', { ascending: false });

        if (result.error) {
        }

        return result;
      } catch (err) {
        return { data: null, error: err };
      }
    },

    async updateStatus(requestId: string, status: 'accepted' | 'rejected') {
      try {
        const result = await supabase
          .from('contact_requests')
          .update({
            status,
            updated_at: new Date().toISOString()
          })
          .eq('id', requestId)
          .select();

        if (result.error) {
        } else {
        }

        return result;
      } catch (err) {
        return { data: null, error: err };
      }
    },

    async checkExistingRequest(studentId: string, tutorId: string) {
      try {
        const result = await supabase
          .from('contact_requests')
          .select('*')
          .eq('student_id', studentId)
          .eq('tutor_id', tutorId)
          .maybeSingle();

        return result;
      } catch (err) {
        return { data: null, error: err };
      }
    }
  }
};
